import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Modifier, ModifierGroup, ModifierOption } from '../../models/order';
import { Store } from 'src/app/models/store';

/** Matches the backend cap (order.sub_modifier_constants.MAX_SUB_MODIFIER_DEPTH). */
const MAX_SUB_MODIFIER_DEPTH = 5;

/**
 * Recursive inline sub-modifier picker for the online ordering flow.
 *
 * Rendered beneath a selected modifier option (inside its `.mod-options`
 * reveal). For each nested group it lists the options as checkboxes; picking
 * one whose own `sub_modifier_groups` exist recurses one level deeper, up to
 * MAX_SUB_MODIFIER_DEPTH.
 *
 * Pure presentation — it holds no selection state. ModifierListComponent owns
 * the flat `selectedModifiers` array; this component reads it to reflect
 * selections and emits `(toggle)` so the list applies add/remove + parent
 * linkage. Only rendered when the `sub_modifiers_enabled` flag is on (gated by
 * the caller), so flag-off stores are byte-identical to before.
 */
@Component({
  selector: 'app-sub-modifier-group',
  templateUrl: './sub-modifier-group.component.html',
  styleUrls: ['./sub-modifier-group.component.scss'],
})
export class SubModifierGroupComponent {
  /** The option these sub-groups hang off — their parent. */
  @Input() parentOption: ModifierOption;

  /** `parentOption.sub_modifier_groups` from the menu payload. */
  @Input() groups: ModifierGroup[] = [];

  /** The flat selected-modifiers array (same reference the list owns). */
  @Input() selectedModifiers: Modifier[] = [];

  @Input() store: Store;

  /** 1 = first sub-level beneath the base modifier. */
  @Input() depth = 1;

  /** Bubbles a tile tap up to ModifierListComponent, which owns the
   *  add/remove logic and parent linkage against the flat array. */
  @Output() toggle = new EventEmitter<{
    option: ModifierOption;
    group: ModifierGroup;
    parentOption: ModifierOption;
  }>();

  readonly maxDepth = MAX_SUB_MODIFIER_DEPTH;

  get canDescend(): boolean {
    return this.depth < this.maxDepth;
  }

  /** "1 REQUIRED" / "REQUIRED" for required groups; '' for optional (no
   *  badge) — required-ness is governed solely by is_required, matching the
   *  top-level group voice. */
  requirementLabel(group: ModifierGroup): string {
    if (group.is_required && group.max_mods > 0) {
      return `${group.max_mods} REQUIRED`;
    }
    if (group.is_required) {
      return 'REQUIRED';
    }
    return '';
  }

  /** Options the customer can pick (POS-only options are hidden online). */
  visibleOptions(group: ModifierGroup): ModifierOption[] {
    return (group.options || []).filter((o) => !o['is_pos_only']);
  }

  /** Selected within THIS parent branch (scoped by parentModifierId) so a
   *  group reused under a sibling option counts independently. */
  isSelected(option: ModifierOption): boolean {
    return this.selectedModifiers.some(
      (m) =>
        m && m.modifier && m.modifier.id === option.id &&
        m.parentModifierId === this.parentOption?.id,
    );
  }

  hasChildren(option: ModifierOption): boolean {
    return Array.isArray(option?.sub_modifier_groups) && option.sub_modifier_groups.length > 0;
  }

  onToggle(group: ModifierGroup, option: ModifierOption): void {
    this.toggle.emit({ option, group, parentOption: this.parentOption });
  }

  trackByGroup = (_: number, g: ModifierGroup) => g.id;
  trackByOption = (_: number, o: ModifierOption) => o.id;
}
